<template>
	<button
		type="button"
		class="inline-flex items-center rounded border-none py-2 w-full justify-between text-left min-h-fit text-sm enabled:hover:bg-base-100 enabled:cursor-pointer opacity-10 enabled:opacity-100 transition-opacity"
		:class="[active? 'bg-base-100' : '']"
		@mousedown.prevent
		@click="handleClick"
	>
		<!-- min-w-0 is needed here -->
		<div class="min-w-0 flex items-center gap-1.5 text-left px-2">
			<!-- Tag Simple Phosphor icon (shown only when showMarker is true) -->
			<div v-if="showMarker">
				<svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" :style="{ fill: hasValidMarkerColor ? collection.color : '#cccccc' }" viewBox="0 0 256 256">
					<path v-if="hasValidMarkerColor" d="M224,128a96,96,0,1,1-96-96A96,96,0,0,1,224,128Z"></path>
					<path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Z"></path>
				</svg>
			</div>

			<!-- content -->
			<!-- min-w-0 is needed here -->
			<div class="min-w-0 max-w-sm flex items-center px-0 py-0">
				<p class="block truncate font-semibold">{{ collection.name }}</p>
				<IconSharedLessonPlanCollection v-if="collection.sharing" :collection="collection" />
			</div>
		</div>

		<!-- right control: badge, chevron and checkbox -->
		<div class="flex items-center ml-2 pr-2 gap-2">
			<div v-if="showCounter && hasCount" class="badge text-white min-w-6 border-0 bg-base-content text-xs" :class="collection.planCount > 0 ? '' : 'opacity-25'">{{ collection.planCount }}</div>
			<template v-if="showChevron">
				<ChevronRightIcon class="w-4 h-4 text-base-content/40" />
			</template>
			<template v-else-if="showCheckbox">
				<span v-if="loading" class="loading loading-spinner loading-md text-accent"></span>
				<input
					v-else
					type="checkbox"
					class="checkbox checkbox-md checkbox-accent pointer-events-none"
					:checked="checked"
				/>
			</template>
		</div>
	</button>
</template>

<script lang="ts">
import { LessonPlanCollection } from '@/models/LessonPlanCollection.model';
import { ChevronRightIcon } from "@heroicons/vue/24/outline";
import IconSharedLessonPlanCollection from "@/components/IconSharedLessonPlanCollection.vue";
import { PropType } from "vue";

export default {
	name: "LessonPlanCollectionsListItem",
	components: { ChevronRightIcon, IconSharedLessonPlanCollection },
	props: {
		collection: {
			type: Object as PropType<LessonPlanCollection>,
			required: true
		},
		showMarker: {
			type: Boolean,
			default: false
		},
		showCounter: {
			type: Boolean,
			default: false
		},
		showChevron: {
			type: Boolean,
			default: false
		},
		showCheckbox: {
			type: Boolean,
			default: false
		},
		checked: {
			type: Boolean,
			default: false
		},
		showShareIcon: {
			type: Boolean,
			default: false
		},
		loading: {
			type: Boolean,
			default: false
		},
		active: {
			type: Boolean,
			default: false
		},
	},
	computed: {
		hasCount(): boolean {
			return typeof this.collection.planCount === "number" && this.collection.planCount !== null;
		},
		hasValidMarkerColor(): boolean {
			if (!this.collection.color) return false;
			// check if valid hex color
			const hexColorRegex = /^#([0-9A-F]{3}){1,2}$/i;
			return hexColorRegex.test(this.collection.color);
		},
	},
	data() {
		return {
			showColorPopover: false,
			colors: [
				"fill-red-400",
				"fill-orange-400",
				"fill-yellow-400",
				"fill-green-400",
				"fill-blue-400",
				"fill-purple-400",
				"fill-pink-400",
				"fill-gray-400",
			],
		};
	},
	methods: {
		handleClick() {
			if (this.showCheckbox) {
				// toggle checkbox when clicking anywhere on the button
				this.$emit("change", !this.checked);
			} else {
				// normal button activation -> emit a named event to avoid colliding
				// with native DOM `click` forwarding on component roots.
				this.$emit("select");
			}
		},
	},
};
</script>
